import { useCallback, useState } from 'react';
import { useToast } from '../contexts/ToastContext';

/**
 * Captures a raw delivery link the moment a single-use token is issued and
 * keeps it in client state keyed by order. The credential is only ever exposed
 * at issue time, so this hook never re-fetches it for the WhatsApp flow —
 * mirroring the "Copiálo ahora" discipline of the reception terminal.
 * Unmounting the page loses the link (rotation is the recovery path), which is
 * the intended single-use behavior.
 */
export function useDeliveryLinks() {
  const { show } = useToast();
  const [links, setLinksState] = useState<Record<string, string>>({});

  const setLink = useCallback((orderId: string, url: string) => {
    setLinksState((current) => ({ ...current, [orderId]: url }));
  }, []);

  const copyLink = useCallback(async (orderId: string) => {
    const link = links[orderId];
    if (!link) return;
    await navigator.clipboard?.writeText(link);
    show('Enlace copiado al portapapeles.', 'success');
  }, [links, show]);

  const shareLink = useCallback(async (orderId: string) => {
    const link = links[orderId];
    if (!link) return;
    if (navigator.share) {
      await navigator.share({ title: 'Enlace de reparto', url: link });
      return;
    }
    await copyLink(orderId);
  }, [copyLink, links]);

  return { links, setLink, copyLink, shareLink };
}
